import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { numberLocale, useLocale, useT, type Translations } from '@/shared/i18n'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'
import { CodeWorkspace } from '@/shared/ui/code-workspace'

const UTILITY = 'url-codec'

type Mode = 'encode' | 'decode'
type UrlCodecTarget = 'url' | 'component' | 'query'

const URL_CODEC_TARGETS: UrlCodecTarget[] = ['url', 'component', 'query']

function encodeUrlInput(input: string, target: UrlCodecTarget, t: Translations): string {
  try {
    if (target === 'url') {
      return encodeURI(input)
    }

    const encoded = encodeURIComponent(input)
    return target === 'query' ? encoded.replace(/%20/g, '+') : encoded
  } catch {
    throw new Error(t.urlCodec.errors.invalidEncoding)
  }
}

function decodeUrlInput(input: string, target: UrlCodecTarget, t: Translations): string {
  try {
    if (target === 'url') {
      return decodeURI(input)
    }

    const normalized = target === 'query' ? input.replace(/\+/g, ' ') : input
    return decodeURIComponent(normalized)
  } catch {
    throw new Error(t.urlCodec.errors.invalidPercent)
  }
}

function processUrlInput(input: string, mode: Mode, target: UrlCodecTarget, t: Translations): string {
  return mode === 'encode'
    ? encodeUrlInput(input, target, t)
    : decodeUrlInput(input, target, t)
}

function lineCount(value: string): number {
  return value ? value.split('\n').length : 0
}

function percentSequenceCount(value: string): number {
  return value.match(/%[0-9a-f]{2}/gi)?.length ?? 0
}

function queryPartCount(value: string): number {
  const trimmed = value.trim().replace(/^\?/, '')
  if (!trimmed) return 0
  return trimmed.split('&').filter(Boolean).length
}

export function UrlCodecPage() {
  const t = useT()
  const { locale } = useLocale()
  const localeCode = numberLocale(locale)
  const [input, setInput] = useState('')
  const [mode, setMode] = useState<Mode>('encode')
  const [target, setTarget] = useState<UrlCodecTarget>('component')
  const [output, setOutput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const handleModeChange = useCallback((nextMode: Mode) => {
    setMode(nextMode)
    setOutput('')
    setError(null)
    setCopied(false)
  }, [])

  const handleTargetChange = useCallback((nextTarget: UrlCodecTarget) => {
    setTarget(nextTarget)
    setOutput('')
    setError(null)
    setCopied(false)
  }, [])

  const handleProcess = useCallback(() => {
    if (!input) {
      setOutput('')
      setError(null)
      setCopied(false)
      return
    }

    const start = performance.now()
    toolInvoke(UTILITY, {
      mode,
      target,
      input_length: input.length,
    })

    try {
      const result = processUrlInput(input, mode, target, t)
      setOutput(result)
      setError(null)
      setCopied(false)
      toolSuccess(UTILITY, Math.round(performance.now() - start), {
        mode,
        target,
        input_length: input.length,
        output_length: result.length,
        percent_sequences: percentSequenceCount(mode === 'encode' ? result : input),
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : t.urlCodec.errors.unknown
      setError(message)
      setOutput('')
      setCopied(false)
      toolError(UTILITY, mode === 'decode' ? 'invalid-percent-encoding' : 'url-encode-failed', {
        mode,
        target,
        input_length: input.length,
      })
    }
  }, [input, mode, t, target])

  useEffect(() => {
    const timeout = window.setTimeout(handleProcess, 200)
    return () => window.clearTimeout(timeout)
  }, [handleProcess])

  const handleCopy = useCallback(async () => {
    if (!output) return
    try {
      await navigator.clipboard.writeText(output)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }, [output])

  const handleSwap = useCallback(() => {
    const nextMode = mode === 'encode' ? 'decode' : 'encode'
    setInput(output)
    setOutput('')
    setError(null)
    setCopied(false)
    setMode(nextMode)
  }, [mode, output])

  const handleClear = useCallback(() => {
    setInput('')
    setOutput('')
    setError(null)
    setCopied(false)
  }, [])

  const inputStats = useMemo(() => ({
    chars: input.length,
    lines: lineCount(input),
    percent: percentSequenceCount(input),
    queryParts: queryPartCount(input),
  }), [input])

  const outputStats = useMemo(() => ({
    chars: output.length,
    lines: lineCount(output),
    percent: percentSequenceCount(output),
    queryParts: queryPartCount(output),
  }), [output])

  return (
    <ToolPageShell toolKey="urlCodec" width="wide">
      <CodeWorkspace
        error={error}
        controls={(
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium tracking-normal">
                {t.urlCodec.mode}
              </span>
              <div className="flex rounded-lg border bg-card p-0.5 shadow-sm">
                <button
                  type="button"
                  onClick={() => handleModeChange('encode')}
                  className={cn(
                    'rounded-md px-4 py-1.5 text-sm font-medium transition-colors',
                    mode === 'encode'
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t.urlCodec.encode}
                </button>
                <button
                  type="button"
                  onClick={() => handleModeChange('decode')}
                  className={cn(
                    'rounded-md px-4 py-1.5 text-sm font-medium transition-colors',
                    mode === 'decode'
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t.urlCodec.decode}
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium tracking-normal">
                {t.urlCodec.target}
              </span>
              <div className="flex flex-wrap rounded-lg border bg-card p-0.5 shadow-sm">
                {URL_CODEC_TARGETS.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => handleTargetChange(item)}
                    className={cn(
                      'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                      target === item
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {t.urlCodec.targets[item]}
                  </button>
                ))}
              </div>
            </div>

          </div>
        )}
        input={{
          title: mode === 'encode' ? t.urlCodec.sourceText : t.urlCodec.encodedText,
          value: input,
          onChange: (value) => {
            setInput(value)
            setOutput('')
            setError(null)
            setCopied(false)
          },
          placeholder: mode === 'encode'
            ? t.urlCodec.inputEncodePlaceholder
            : t.urlCodec.inputDecodePlaceholder,
          ariaLabel: mode === 'encode'
            ? t.urlCodec.encodeLabel
            : t.urlCodec.decodeLabel,
          clear: { label: t.urlCodec.clear, onClick: handleClear },
          stats: [
            {
              label: t.urlCodec.characters,
              value: inputStats.chars.toLocaleString(localeCode),
            },
            {
              label: t.urlCodec.lines,
              value: inputStats.lines.toLocaleString(localeCode),
            },
            {
              label: t.urlCodec.percentSequences,
              value: inputStats.percent.toLocaleString(localeCode),
            },
          ],
        }}
        output={{
          title: t.urlCodec.resultTitle,
          value: error ? '' : output,
          readOnly: true,
          placeholder: t.urlCodec.outputPlaceholder,
          ariaLabel: t.urlCodec.resultLabel,
          actions: (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopy}
              >
                {copied ? t.urlCodec.copied : t.urlCodec.copy}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSwap}
              >
                {t.urlCodec.swap}
              </Button>
            </>
          ),
          stats: output && !error
            ? [
              {
                label: t.urlCodec.characters,
                value: outputStats.chars.toLocaleString(localeCode),
              },
              {
                label: t.urlCodec.lines,
                value: outputStats.lines.toLocaleString(localeCode),
              },
              {
                label: t.urlCodec.percentSequences,
                value: outputStats.percent.toLocaleString(localeCode),
              },
            ]
            : undefined,
        }}
      />

      {output && (
        <section className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Card className="shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm font-medium tracking-normal">
                {t.urlCodec.percentSequences}
              </div>
              <div className="mt-1 text-2xl font-bold tabular-nums">
                {outputStats.percent.toLocaleString(localeCode)}
              </div>
              <div className="text-xs text-muted-foreground">
                {t.urlCodec.inResult}
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm font-medium tracking-normal">
                {t.urlCodec.queryParts}
              </div>
              <div className="mt-1 text-2xl font-bold tabular-nums">
                {(target === 'query' ? outputStats.queryParts : inputStats.queryParts)
                  .toLocaleString(localeCode)}
              </div>
              <div className="text-xs text-muted-foreground">
                {t.urlCodec.simpleQueryHint}
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm font-medium tracking-normal">
                {t.urlCodec.processing}
              </div>
              <div className="mt-1 text-2xl font-bold tabular-nums">
                {t.urlCodec.targets[target]}
              </div>
              <div className="text-xs text-muted-foreground">
                {t.urlCodec.localOnly}
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      <section className="mt-10 space-y-5 border-t pt-8">
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.urlCodec.howTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.urlCodec.how}
          </p>
        </div>
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.urlCodec.whenTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.urlCodec.when}
          </p>
        </div>
      </section>
    </ToolPageShell>
  )
}
